import {
  isPrairiTeamParticipant,
  SYNTHETIC_PRAIRI_TEAM_CONVERSATION_ID,
} from '../constants/prairiTeamWelcomeMessage';

const SESSION_BOOT_KEY = 'prairi.unreadFounderTip.sessionBoot';
const EPISODE_PREFIX = 'prairi.unreadFounderTip.episode.';

/** Sessions with the same unread episode before the tip may appear. */
export const UNREAD_FOUNDER_TIP_SESSION_THRESHOLD = 3;

export type UnreadFounderTipEpisode = {
  fingerprint: string;
  sessions: number;
  shown: boolean;
  dismissed: boolean;
};

export type FounderUnreadConversation = {
  conversationId: string;
  unreadCount?: number;
  lastMessageAt?: string | null;
  otherUser?: { name?: string | null; companyName?: string | null };
};

/** Real founder threads with unread — excludes PRAIRI Team (including synthetic welcome). */
export function founderUnreadConversations(
  conversations: FounderUnreadConversation[]
): FounderUnreadConversation[] {
  return conversations.filter(item => {
    if (Number(item.unreadCount || 0) <= 0) return false;
    if (item.conversationId === SYNTHETIC_PRAIRI_TEAM_CONVERSATION_ID) return false;
    if (isPrairiTeamParticipant(item.otherUser || {})) return false;
    return true;
  });
}

/** Stable id for “this unread-message episode” (same threads + same last messages). */
export function founderUnreadEpisodeFingerprint(
  conversations: FounderUnreadConversation[]
): string | null {
  const unread = founderUnreadConversations(conversations);
  if (unread.length === 0) return null;
  return unread
    .map(item => `${item.conversationId}:${item.lastMessageAt ?? ''}`)
    .sort()
    .join('|');
}

export function readUnreadFounderTipEpisode(userId: string): UnreadFounderTipEpisode | null {
  try {
    const raw = window.localStorage.getItem(`${EPISODE_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<UnreadFounderTipEpisode>;
    if (typeof parsed.fingerprint !== 'string' || !parsed.fingerprint) return null;
    return {
      fingerprint: parsed.fingerprint,
      sessions: Number(parsed.sessions) > 0 ? Number(parsed.sessions) : 1,
      shown: parsed.shown === true,
      dismissed: parsed.dismissed === true,
    };
  } catch {
    return null;
  }
}

export function writeUnreadFounderTipEpisode(
  userId: string,
  episode: UnreadFounderTipEpisode
): void {
  try {
    window.localStorage.setItem(`${EPISODE_PREFIX}${userId}`, JSON.stringify(episode));
  } catch {
    // Private mode — tip logic still works for this tab via returned state.
  }
}

function isNewBrowserSession(): boolean {
  try {
    if (window.sessionStorage.getItem(SESSION_BOOT_KEY)) return false;
    window.sessionStorage.setItem(SESSION_BOOT_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * Keep episode session count in sync with the current founder-unread fingerprint.
 * A new browser session while the same episode is still unread increments the count.
 */
export function syncUnreadFounderTipEpisode(
  userId: string,
  fingerprint: string | null
): UnreadFounderTipEpisode | null {
  if (!fingerprint) return null;

  const newSession = isNewBrowserSession();
  const existing = readUnreadFounderTipEpisode(userId);

  if (!existing || existing.fingerprint !== fingerprint) {
    const next: UnreadFounderTipEpisode = {
      fingerprint,
      sessions: 1,
      shown: false,
      dismissed: false,
    };
    writeUnreadFounderTipEpisode(userId, next);
    return next;
  }

  if (newSession) {
    const next: UnreadFounderTipEpisode = {
      ...existing,
      sessions: existing.sessions + 1,
    };
    writeUnreadFounderTipEpisode(userId, next);
    return next;
  }

  return existing;
}

export function markUnreadFounderTipShown(userId: string, fingerprint: string): void {
  const existing = readUnreadFounderTipEpisode(userId);
  const next: UnreadFounderTipEpisode = {
    fingerprint,
    sessions: existing?.fingerprint === fingerprint ? existing.sessions : 1,
    shown: true,
    dismissed: existing?.fingerprint === fingerprint ? existing.dismissed : false,
  };
  writeUnreadFounderTipEpisode(userId, next);
}

export function markUnreadFounderTipDismissed(userId: string, fingerprint: string): void {
  const existing = readUnreadFounderTipEpisode(userId);
  const next: UnreadFounderTipEpisode = {
    fingerprint,
    sessions: existing?.fingerprint === fingerprint ? existing.sessions : 1,
    shown: true,
    dismissed: true,
  };
  writeUnreadFounderTipEpisode(userId, next);
}

export function shouldShowUnreadFounderMessageTip(args: {
  isInvestor: boolean;
  catalogHome: boolean;
  reviewingStartup: boolean;
  competingPopup: boolean;
  fingerprint: string | null;
  episode: UnreadFounderTipEpisode | null;
}): boolean {
  if (!args.isInvestor || !args.catalogHome || args.reviewingStartup || args.competingPopup) {
    return false;
  }
  if (!args.fingerprint || !args.episode) return false;
  if (args.episode.fingerprint !== args.fingerprint) return false;
  if (args.episode.shown || args.episode.dismissed) return false;
  return args.episode.sessions >= UNREAD_FOUNDER_TIP_SESSION_THRESHOLD;
}
